'use client';

import { Icon, IconType } from '../icons';
import { cn, TagProps, TagVariant, TTagSeverity } from 'types';

const disabledStyles = 'bg-neutralGrey text-neutralPlus border border-neutralPlus cursor-not-allowed opacity-60';

const variantStyles: Record<TagVariant, string> = {
  light: 'bg-neutralGrey border border-neutralGrey hover:border-concrete focus:border-concrete focus:border-2',
  outline:
    'bg-neutralGrey border border-neutralPlus text-black hover:border-concrete focus:border-concrete focus:border-2',
  purple: 'bg-darkBlue text-white hover:bg-ultramarine focus:ring-blue60 focus:ring-2',
};

const severityStyles: Record<TTagSeverity, string> = {
  [TTagSeverity.NEUTRAL]: 'bg-neutralGrey border-neutralPlus',
  [TTagSeverity.SUCCESS]: 'bg-komorebi20 border-komorebi60',
  [TTagSeverity.WARNING]: 'bg-oker20 border-oker60',
  [TTagSeverity.ERROR]: 'bg-lightDestructive border-poppy60',
  [TTagSeverity.INFORMATION]: 'bg-blue20 border-blue',
};
const severityStatesStyles: Record<TTagSeverity, string> = {
  [TTagSeverity.NEUTRAL]: 'hover:enabled:border-concrete active:enabled:border-black active:enabled:bg-neutralPlus',
  [TTagSeverity.SUCCESS]: 'hover:enabled:border-concrete active:enabled:border-black active:enabled:bg-komorebi60',
  [TTagSeverity.WARNING]: 'hover:enabled:border-concrete active:enabled:border-black active:enabled:bg-oker60',
  [TTagSeverity.ERROR]: 'hover:enabled:border-concrete active:enabled:border-black active:enabled:bg-poppy60',
  [TTagSeverity.INFORMATION]:
    'hover:enabled:bg-darkBlue hover:enabled:text-white hover:enabled:border-darkBlue active:enabled:border-black active:enabled:bg-blue60',
};

export function Tag({
  children,
  variant = 'light',
  closable = false,
  disabled = false,
  className,
  severity = undefined,
  onClose,
  onClick,
}: TagProps) {
  const isClickable = Boolean(typeof onClick === 'function');
  const dynamicTagClasses =
    severity !== undefined
      ? cn(
          'border disabled:opacity-60 disabled:cursor-default',
          severityStyles[severity],
          isClickable ? severityStatesStyles[severity] : ''
        )
      : variantStyles[variant];

  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'w-fit group inline-flex items-center gap-2 rounded-full',
        closable ? 'pl-3 pr-1' : 'px-3',
        'text-xs leading-[130%] transition-colors',
        'focus:outline-none',
        disabled && severity === undefined ? disabledStyles : dynamicTagClasses,
        isClickable ? 'cursor-pointer' : '',
        className
      )}
      onClick={isClickable ? onClick : undefined}
    >
      {children}

      {closable && (
        <span className="flex items-center">
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            aria-label="Remove tag"
            className={cn(
              'flex items-center justify-center w-5 h-5 rounded-full cursor-pointer',
              disabled ? 'bg-white cursor-not-allowed' : 'bg-white border border-white'
            )}
          >
            <Icon
              icon={IconType.Close}
              fill="currentColor"
              width={16}
              height={16}
              className={cn('transition-colors', disabled ? 'text-neutralPlus' : 'text-black group-hover:text-poppy')}
            />
          </button>
        </span>
      )}
    </button>
  );
}
